import React, { useState } from 'react'
import { getCurrentLocation, getZipCoordinates, Coordinates } from '../utils/distanceCalculator'

interface LocationInputProps {
  onLocationChange: (coordinates: Coordinates | null, userInput: string) => void
  isLoading: boolean
}

const LocationInput: React.FC<LocationInputProps> = ({ onLocationChange, isLoading }) => {
  const [zipCode, setZipCode] = useState('')
  const [gettingLocation, setGettingLocation] = useState(false)
  const [locationError, setLocationError] = useState<string | null>(null)
  const [lastUsedLocation, setLastUsedLocation] = useState<string | null>(null)

  const handleZipCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const cleanZip = zipCode.trim().replace(/\D/g, '').substring(0, 5)

    if (cleanZip.length !== 5) {
      setLocationError('Please enter a valid 5-digit zip code')
      return
    }

    setLocationError(null)
    setGettingLocation(true)

    try {
      // Try to get exact coordinates from API
      const coordinates = await getZipCoordinates(cleanZip)

      if (coordinates) {
        setLastUsedLocation(`ZIP ${cleanZip}`)
        onLocationChange(coordinates, cleanZip)
      } else {
        setLocationError('Unable to locate this zip code')
      }
    } catch (error) {
      setLocationError('Error looking up zip code')
    } finally {
      setGettingLocation(false)
    }
  }

  const handleGetCurrentLocation = async () => {
    setGettingLocation(true)
    setLocationError(null)

    try {
      const coordinates = await getCurrentLocation()

      if (coordinates) {
        setLastUsedLocation('Current location')
        onLocationChange(coordinates, 'current')
      } else {
        setLocationError('Location access denied or unavailable')
      }
    } catch (error) {
      setLocationError('Failed to get your location')
    } finally {
      setGettingLocation(false)
    }
  }

  const handleClearLocation = () => {
    setZipCode('')
    setLocationError(null)
    setLastUsedLocation(null)
    onLocationChange(null, '')
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Zip Code Input */}
        <form onSubmit={handleZipCodeSubmit} className="flex-1">
          <div className="flex gap-2">
            <input
              type="text"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              placeholder="Enter your zip code"
              className="flex-1 px-4 py-3 sm:px-3 sm:py-2 border border-white/20 rounded-md text-sm bg-[#1a1a1a] text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent min-h-11 sm:min-h-auto"
              maxLength={5}
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={isLoading || zipCode.trim().length < 5}
              className="px-6 py-3 sm:px-4 sm:py-2 bg-rose-600 text-white rounded-xl text-sm font-medium hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-11 sm:min-h-auto flex items-center justify-center"
              style={{ boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)' }}
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                'Sort'
              )}
            </button>
          </div>
        </form>

        {/* Location Button */}
        <button
          onClick={handleGetCurrentLocation}
          disabled={gettingLocation || isLoading}
          className="px-6 py-3 sm:px-4 sm:py-2 bg-neutral-700 text-white rounded-xl text-sm font-medium hover:bg-neutral-600 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 min-h-11 sm:min-h-auto"
        >
          {gettingLocation ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          )}
          <span className="hidden sm:inline">Use My Location</span>
          <span className="sm:hidden">Location</span>
        </button>

        {/* Clear Button */}
        {lastUsedLocation && (
          <button
            onClick={handleClearLocation}
            disabled={isLoading}
            className="px-4 py-3 sm:px-3 sm:py-2 text-neutral-400 hover:text-white transition-colors text-sm min-h-11 sm:min-h-auto flex items-center justify-center"
          >
            Clear
          </button>
        )}
      </div>

      {/* Status Messages */}
      {locationError && (
        <div className="flex items-center space-x-2 text-sm text-red-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{locationError}</span>
        </div>
      )}

      {lastUsedLocation && !locationError && (
        <div className="flex items-center space-x-2 text-sm text-green-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>Sorting by distance from {lastUsedLocation}</span>
        </div>
      )}

      {/* Instructions */}
      {!lastUsedLocation && !locationError && (
        <div className="text-sm text-neutral-500">
          Enter your zip code or use your current location to sort cars by distance
        </div>
      )}
    </div>
  )
}

export default LocationInput
